import { Box, ColorSwatchGroup, Typography } from '@customink/pigment-react';
import {
  type SelectedSwatch,
  useProduct,
  useProjectItems,
} from '@customink/project-service-react-components';
import groupBy from 'lodash/groupBy';
import { useEffect, useState } from 'react';

import { getSwatchData } from './util';

import { spacing4, spacing8 } from '@/utils/designTokens';
import { pageViewTag } from '@/utils/metrics';

// TODO: Confirm whether this should be the product bulk minimum instead
const SINGLES_THRESHOLD = 6;

const swatchGroupSxProps = {
  padding: '0',
  paddingBottom: spacing8,
  paddingTop: spacing4,
};

type AddItemColorSwatchGroupProps = {
  colorId?: number;
  designCid?: string;
  onChange: (colorId: number) => void;
};

export function AddItemColorSwatchGroup({
  colorId,
  designCid,
  onChange,
}: AddItemColorSwatchGroupProps) {
  const { data: projectItems } = useProjectItems();

  const { data: product } = useProduct({
    colorId,
  });

  const color = product?.colors.find((color) => color.id === colorId);

  const [selectedSwatch, setSelectedSwatch] = useState<SelectedSwatch>({
    colorId: colorId || 0,
    name: color?.name || '',
  });

  useEffect(() => {
    setSelectedSwatch({
      colorId: colorId || 0,
      name: color?.name || '',
    });
  }, [colorId, color]);

  const selectedSwatches = (product?.swatches || []).filter((swatch) => {
    return projectItems?.some(
      (item) =>
        item.design?.cid === designCid &&
        item.product?.colorId === swatch.colorId,
    );
  });

  const totalQuantity = (projectItems || [])
    ?.filter((item) => item.design?.cid === designCid)
    .reduce((acc, item) => acc + (item?.totalQuantity || 0), 0);

  const { true: singlesSwatches, false: swatches } = groupBy(
    product?.swatches,
    'singlesEnabled',
  );

  const handleSwatchSelect = ({ value }: { value?: SelectedSwatch }) => {
    if (
      !value ||
      selectedSwatches.map((swatch) => swatch.colorId).includes(value.colorId)
    ) {
      setSelectedSwatch(selectedSwatch);
      onChange(selectedSwatch.colorId);
      return;
    }

    setSelectedSwatch(value);
    onChange(value.colorId);
    pageViewTag(`checkout/popup/addcolor-selectcolor`);
  };

  const singlesSwatchData = getSwatchData({
    selectedColorId: selectedSwatch?.colorId,
    selectedSwatches: selectedSwatches || [],
    swatches: singlesSwatches,
  });

  const bulkSwatchData = getSwatchData({
    selectedColorId: selectedSwatch?.colorId,
    selectedSwatches: selectedSwatches || [],
    swatches,
  });

  const helperText =
    totalQuantity >= SINGLES_THRESHOLD
      ? 'available for your order!'
      : totalQuantity === SINGLES_THRESHOLD - 1
      ? '1 more item needed'
      : `${SINGLES_THRESHOLD - totalQuantity} more items needed`;

  return (
    <Box data-testid="color-swatch-group">
      {!!singlesSwatches?.length && (
        <>
          <Typography>Color choices for any size order</Typography>
          <Box data-testid="swatch">
            <ColorSwatchGroup
              defaultSelected={singlesSwatchData.some(
                (swatch) => swatch.startSelected,
              )}
              onSwatchSelect={handleSwatchSelect}
              swatchData={singlesSwatchData}
              sx={swatchGroupSxProps}
            />
          </Box>
        </>
      )}

      {!!swatches?.length && (
        <>
          <Typography>
            More choices for orders totaling 6 or more
            {!!helperText && <span>{` (${helperText})`}</span>}
          </Typography>
          <Box data-testid="swatch">
            <ColorSwatchGroup
              defaultSelected={bulkSwatchData.some(
                (swatch) => swatch.startSelected,
              )}
              onSwatchSelect={handleSwatchSelect}
              swatchData={bulkSwatchData}
              sx={swatchGroupSxProps}
            />
          </Box>
        </>
      )}

      <Typography>Selected Color: {selectedSwatch?.name}</Typography>
    </Box>
  );
}
